import { normalizeCourseBaseName } from './courseProgression';

/**
 * 🎯 CONFIGURACIÓN DE SEPARACIÓN DE DIMENSIONES POR CURSO
 * 
 * Este archivo define cuándo las dimensiones madres se separan en subdimensiones
 * según el curso del estudiante.
 * 
 * 📚 REGLAS DE SEPARACIÓN:
 * - separationCourse: El curso desde el cual se separan las subdimensiones
 * - beforeSeparation: "mother" = solo mostrar dimensión madre
 * - afterSeparation: "children" = solo mostrar subdimensiones (madre desaparece)
 */

export interface DimensionSeparationRule {
  dimensionId: number;
  dimensionName: string;
  separationCourse: string; // Curso desde el cual se separan
  beforeSeparation: 'mother' | 'children' | 'both';
  afterSeparation: 'mother' | 'children' | 'both';
}

/**
 * 🔧 CONFIGURACIÓN PRINCIPAL - EDITAR AQUÍ PARA CAMBIOS FUTUROS
 */
export const DIMENSION_SEPARATION_RULES: DimensionSeparationRule[] = [
  {
    dimensionId: 3, // Habilidades comunicacionales en el lenguaje español
    dimensionName: "Habilidades comunicacionales en el lenguaje español",
    separationCourse: "Tercero básico", // Se separa desde Primero básico
    beforeSeparation: "mother", // Hasta Kinder: solo madre
    afterSeparation: "children" // Primero básico+: solo hijas
  },
  {
    dimensionId: 5, // Lenguaje Artístico
    dimensionName: "Lenguaje Artístico",
    separationCourse: "Primero básico", // Se separa desde Primero básico
    beforeSeparation: "mother", // Hasta Kinder: solo madre
    afterSeparation: "children" // Primero básico+: solo hijas
  },
  {
    dimensionId: 7, // Encuentro con la Naturaleza y Sociedad (ID CORREGIDO)
    dimensionName: "Encuentro con la Naturaleza y Sociedad",
    separationCourse: "Quinto básico", // Se separa desde Primero básico
    beforeSeparation: "mother", // Hasta Kinder: solo madre
    afterSeparation: "children" // Primero básico+: solo hijas
  }
];

/**
 * 📋 ORDEN DE CURSOS PARA COMPARACIÓN
 */
const COURSE_ORDER = [
  "Sala Cuna Menor",
  "Sala Cuna Mayor", 
  "Medio Menor",
  "Medio Mayor",
  "Pre Kinder",
  "Kinder",
  "Primero básico",
  "Segundo básico",
  "Tercero básico",
  "Cuarto básico",
  "Quinto básico",
  "Sexto básico",
  "Séptimo básico",
  "Octavo básico",
  "Primero medio",
  "Segundo medio",
  "Tercero medio",
  "Cuarto medio"
];

/**
 * 🔍 UTILIDADES PARA EVALUACIÓN DE CURSOS
 */
export function getCourseIndex(course: string): number {
  // Prefer robust normalization (handles "3° básico A", accents, etc.)
  const baseName = normalizeCourseBaseName(course);
  if (baseName) {
    const byBase = COURSE_ORDER.indexOf(baseName);
    if (byBase !== -1) return byBase;
  }

  // Fallback: strip section letter A-F (upper or lower)
  const normalizedCourse = course.replace(/\s+[A-Za-z]$/, '').trim();
  const index = COURSE_ORDER.indexOf(normalizedCourse);

  return index === -1 ? 0 : index;
}

export function isCourseEqualOrAfter(studentCourse: string, targetCourse: string): boolean {
  return getCourseIndex(studentCourse) >= getCourseIndex(targetCourse);
}

/**
 * 🎯 FUNCIÓN PRINCIPAL: Obtener configuración para una dimensión
 */
export function getDimensionSeparationConfig(dimensionId: number): DimensionSeparationRule | null {
  return DIMENSION_SEPARATION_RULES.find(rule => rule.dimensionId === dimensionId) || null;
}

/**
 * 🧠 FUNCIÓN PRINCIPAL: Determinar cómo mostrar una dimensión
 */
export function shouldShowDimensionAndSubdimensions(
  dimensionId: number, 
  studentCourse: string
): { showMother: boolean; showChildren: boolean } {
  
  const rule = getDimensionSeparationConfig(dimensionId);
  
  if (!rule) {
    // Si no hay regla especial, usar comportamiento por defecto
    return { showMother: true, showChildren: true };
  }
  
  const shouldSeparate = isCourseEqualOrAfter(studentCourse, rule.separationCourse);
  
  if (shouldSeparate) {
    // Después del curso de separación
    return {
      showMother: rule.afterSeparation === 'mother' || rule.afterSeparation === 'both',
      showChildren: rule.afterSeparation === 'children' || rule.afterSeparation === 'both'
    };
  } else {
    // Antes del curso de separación
    return {
      showMother: rule.beforeSeparation === 'mother' || rule.beforeSeparation === 'both',
      showChildren: rule.beforeSeparation === 'children' || rule.beforeSeparation === 'both'
    };
  }
}

/**
 * 📝 DOCUMENTACIÓN PARA CAMBIOS FUTUROS:
 * 
 * Para modificar cuándo se separan las dimensiones:
 * 1. Editar DIMENSION_SEPARATION_RULES arriba
 * 2. Cambiar separationCourse al curso deseado
 * 3. Ajustar beforeSeparation/afterSeparation si es necesario
 * 
 * Para agregar nuevas dimensiones con reglas especiales:
 * 1. Agregar nuevo objeto a DIMENSION_SEPARATION_RULES
 * 2. Definir dimensionId, separationCourse y comportamiento
 * 
 * Para cambiar el orden de cursos:
 * 1. Editar COURSE_ORDER arriba
 */
